// src/components/client/StylistHeader.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Stylist } from '../../types/stylist';
import stylistService from '../../services/stylistService';
import { useIsSignedIn, useRequireSignIn } from '../../utils/guestAccess';
import { resolveWebImageSrc } from '../../utils/resolveMediaUrl';
import {
  StylistLiveReviewSummary,
  getEmbeddedStylistRating,
  ratingFromLiveSummary,
} from '../../utils/stylistLiveReviewSummary';

export type { StylistLiveReviewSummary };

interface StylistHeaderProps {
  stylist: Stylist;
  /** When provided, overrides embedded stylist.rating / reviewCount once the reviews API responds. */
  liveReviewSummary?: StylistLiveReviewSummary;
}

// Define the shape of the new bio object
interface BioObject {
  shortBio?: string;
  fullBio?: string;
}

const StylistHeader: React.FC<StylistHeaderProps> = ({ stylist, liveReviewSummary }) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 640);
  const [isFavorite, setIsFavorite] = useState<boolean>(stylist.isFavorite || false);
  const isSignedIn = useIsSignedIn();
  const requireSignIn = useRequireSignIn();

  const embedded = getEmbeddedStylistRating(stylist);
  const { average: ratingAvg, count: ratingCount, loading: summaryLoading } =
    ratingFromLiveSummary(embedded, liveReviewSummary);

  // Compute a safe string to render for the bio
  const bioText = useMemo<string>(() => {
    const raw = stylist.bio;
    if (!raw) {
      return '';
    }
    // If it's a string, render it directly
    if (typeof raw === 'string') {
      return raw;
    }
    // Otherwise assume it's our BioObject
    const obj = raw as BioObject;
    return obj.shortBio ?? obj.fullBio ?? '';
  }, [stylist.bio]);

  // Handle window resize for mobile layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle favorite/unfavorite
  const handleFavoriteToggle = async () => {
    if (!isSignedIn) {
      requireSignIn('favorites');
      return;
    }
    try {
      if (isFavorite) {
        await stylistService.removeFavorite(stylist._id);
      } else {
        await stylistService.addFavorite(stylist._id);
      }
      setIsFavorite(!isFavorite);
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Web share or copy link fallback
  const handleShare = () => {
    const shareData = {
      title: `${stylist.name} - LookBel Profile`,
      text: `Check out ${stylist.name}'s styling services on LookBel!`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Link copied to clipboard!'))
        .catch(() => console.error('Failed to copy link'));
    }
  };

  return (
    <div className="stylist-header">
      <div className={`stylist-header__container ${isMobile ? 'mobile' : ''}`}>
        {/* Avatar */}
        <div className="stylist-header__avatar">
          {stylist.profilePhoto?.url ? (
            <img
              src={resolveWebImageSrc(stylist.profilePhoto.url)}
              alt={stylist.name}
            />
          ) : (
            <div className="stylist-header__avatar-initials" aria-label={stylist.name}>
              {stylist.name?.charAt(0).toUpperCase() ?? '?'}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="stylist-header__content">
          <div className="stylist-header__top">
            <div className="stylist-header__info">
              <h1 className="stylist-header__name">{stylist.name}</h1>
              <div className="stylist-header__rating">
                {summaryLoading ? (
                  <span
                    className="stylist-header__reviews"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    …
                  </span>
                ) : ratingCount > 0 ? (
                  <>
                    <div className="stylist-header__stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`stylist-header__star ${
                            star <= Math.round(ratingAvg) ? 'active' : ''
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <span className="stylist-header__reviews">
                      {ratingAvg.toFixed(1)} ({ratingCount} reviews)
                    </span>
                  </>
                ) : (
                  <span className="stylist-header__reviews">No reviews yet</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="stylist-header__actions">
              <button
                className={`stylist-header__action-btn stylist-header__favorite-btn ${
                  isFavorite ? 'active' : ''
                }`}
                onClick={handleFavoriteToggle}
                aria-label={
                  isFavorite ? 'Remove from favorites' : 'Add to favorites'
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={isFavorite ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </button>

              <button
                className="stylist-header__action-btn stylist-header__share-btn"
                onClick={handleShare}
                aria-label="Share profile"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
              </button>
            </div>
          </div>

          {/* Location */}
          {stylist.location && (
            <div className="stylist-header__location">
              <svg
                className="stylist-header__location-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span className="stylist-header__location-text">
                {stylist.location.city}, {stylist.location.state}
                {stylist.distance &&
                  ` • ${
                    typeof stylist.distance === 'number'
                      ? `${stylist.distance.toFixed(1)} miles away`
                      : stylist.distance
                  }`}
              </span>
            </div>
          )}

          {/* Specialties */}
          <div className="stylist-header__specialties">
            {(stylist.specialties || []).slice(0, 5).map((category, idx) => (
              <span key={idx} className="stylist-header__specialty">
                {category
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
            ))}
          </div>

          {/* Bio */}
          {bioText && <p className="stylist-header__bio">{bioText}</p>}
        </div>
      </div>
    </div>
  );
};

export default StylistHeader;
